import {
  Address,
  newAddress,
} from "@/services/backend/addresses/types/address";
import { Contact, newContact } from "@/services/backend/contacts/types/contact";
import { UUID } from "@bitory-gmbh/nidavellir";

/** Mirrors `document.ServiceObjectReferencesBase`, backend/core/document/domain/document/service_object_references_base.go. */
export type ServiceObjectReferencesBase = {
  serviceObjects: ServiceObjectReference[];
};

/** Mirrors `document.ServiceObjectReference`, backend/core/document/domain/document/service_object_references_base.go. */
export type ServiceObjectReference = {
  serviceObjectId: UUID;
  serviceObjectAddress: Address;
  serviceContact: Contact;
};

export function addServiceObject(
  references: ServiceObjectReferencesBase,
  serviceObjectId: string,
): ServiceObjectReferencesBase {
  const { serviceObjects } = references;
  if (serviceObjects.some((s) => s.serviceObjectId === serviceObjectId)) {
    return { serviceObjects };
  }

  return {
    serviceObjects: [
      ...serviceObjects,
      newServiceObjectReference(serviceObjectId),
    ],
  };
}

export function newServiceObjectReference(id: UUID): ServiceObjectReference {
  return {
    serviceObjectId: id,
    serviceObjectAddress: newAddress(),
    serviceContact: newContact(),
  };
}

export function updateServiceObjectsByIds(
  references: ServiceObjectReferencesBase,
  newIds: UUID[],
): ServiceObjectReferencesBase {
  return {
    serviceObjects: [
      // we add the existing first, but filter
      // those whos id is no longer present
      ...references.serviceObjects.filter((so) =>
        newIds.includes(so.serviceObjectId),
      ),
      // add new service object reference for new ids
      ...newIds
        .filter(
          // filter what was already in
          (sId) =>
            !references.serviceObjects.some((so) => so.serviceObjectId === sId),
        )
        .map((sId) => newServiceObjectReference(sId)),
    ],
  };
}

export function removeServiceObject(
  references: ServiceObjectReferencesBase,
  serviceObjectId: string,
): ServiceObjectReferencesBase {
  return {
    serviceObjects: references.serviceObjects.filter(
      (s) => s.serviceObjectId !== serviceObjectId,
    ),
  };
}

export function updateServiceObjectAddress(
  references: ServiceObjectReferencesBase,
  serviceObjectId: string,
  serviceObjectAddress: Address,
): ServiceObjectReferencesBase {
  return {
    serviceObjects: references.serviceObjects.map((s) =>
      s.serviceObjectId === serviceObjectId
        ? { ...s, serviceObjectAddress }
        : s,
    ),
  };
}

export function updateServiceObjectContact(
  references: ServiceObjectReferencesBase,
  serviceObjectId: string,
  serviceContact: Contact,
): ServiceObjectReferencesBase {
  return {
    serviceObjects: references.serviceObjects.map((s) =>
      s.serviceObjectId === serviceObjectId ? { ...s, serviceContact } : s,
    ),
  };
}
